import { buildUvInsertCartLineItem, type UvInsertCartLineItem } from '@shared/view/uv-inserts/build-cart-line-item';
import { resolveUvInserts, type ResolvedUvInserts } from '@shared/view/uv-inserts/resolve-uv-inserts';
import { readUvInsertsBootstrapPayload } from '@uv-inserts/read-uv-inserts-bootstrap-payload';
import { Addon, ChoicePayload } from './addToCart';
import { buildPersonalisationChoices, isStoryOfUsCardChoice } from './buildPersonalisationChoices';
import { supportsWebP } from '../utilities/webpDetection';
import { convertCartPayloadToFormData } from '../utilities/atcViaFormData';
import { logError } from '../utilities/logging';
import { validateCartItem } from './atcValidation';
import { imagePropertiesProcessing } from '../utilities/imagePropertiesProcessing';
import { fetchVendor } from './queries/fetchVendor';
import { addBaseGid, removeGid } from '../utilities/handleGid';
import { base64ToFile } from '../utilities/base64ToFile';
import { Selections } from '../context/personalisation-typedefs';
import { Slot } from './personalisationData';
import { CART_VERSION_KEY } from '../../../base/cart-version';
import { generateAlphaNumeric } from '../../../gift-wrap/src/utilities/randomKey';

const imageType = supportsWebP() ? 'image/webp' : 'image/png';

/**
 * The Story of Us keepsake card and its envelope as their own cart lines, linked to the piece by a gift wrap key,
 * the way the gift wrap flow adds an insert (ALBAU-909). The card line is the shared gift wrap insert builder's,
 * so it gets the same key, parent title, template and vendor properties and its config's own text and photo keys.
 * The card also carries what a gift wrap insert does not: its timeline rows (TEXT_LIST keys the host derives,
 * which the builder leaves to the host) and the rasterised "Keepsake card" picture. Both are added from the
 * selection, so every value the card choice carried before ends up on the card line. The envelope carries the
 * key, parent title and vendor only.
 *
 * @throws When the card's config or design is not on the page: the selection was made from it, so the page is
 *   broken, and adding the piece alone would silently drop the card the customer made.
 */
const buildStoryOfUsCardLines = ({
  choice,
  uvInserts,
  giftWrapKey,
  parentTitle,
  vendor
}: {
  choice: ChoicePayload;
  uvInserts: ResolvedUvInserts;
  giftWrapKey: string;
  parentTitle: string | undefined;
  vendor: string | null | undefined;
}): UvInsertCartLineItem[] => {
  const selection = (choice as any).uvInsertSelection as {
    variantId: number;
    configHandle: string;
    templateId?: string | null;
    textByKey?: Record<string, string>;
    imageByKey?: Record<string, File | null>;
  };
  const config = uvInserts.configs.find(({ handle }) => handle === selection.configHandle);
  // The builder takes a design index and clamps it, so look the picked variant up rather than risk another design.
  const themeIndex = config?.themes.findIndex(({ id }) => Number(id) === Number(selection.variantId)) ?? -1;
  const card =
    config && themeIndex >= 0
      ? buildUvInsertCartLineItem({
          config,
          themeIndex,
          templateIds: uvInserts.templateIds,
          textByKey: selection.textByKey || {},
          imageByKey: selection.imageByKey || {},
          giftWrapKey,
          parentTitle: parentTitle || '',
          vendor
        })
      : null;
  if (!card) {
    logError({
      label: 'addToCart',
      error: new Error('Story of Us card config or design not found'),
      message: 'Story of Us card line could not be built',
      extraData: { configHandle: selection.configHandle, variantId: selection.variantId }
    });
    throw new Error('Story of Us card line could not be built');
  }

  if (!parentTitle) delete card.properties._giftwrap_parent_title;
  // The builder only looks the template up by exact variant id; the selection's was resolved more leniently
  // (resolveTemplateIdByVariantId), and the print partner needs it on the card line.
  if (!card.properties._template_id && typeof selection.templateId === 'string' && selection.templateId.trim() !== '') {
    card.properties._template_id = selection.templateId;
  }
  for (const [key, value] of Object.entries(selection.textByKey || {})) {
    if (!(key in card.properties) && typeof value === 'string' && value.trim() !== '') card.properties[key] = value;
  }
  for (const [key, value] of Object.entries(selection.imageByKey || {})) {
    if (!(key in card.properties) && value instanceof File) card.properties[key] = value;
  }

  const addOnVariantIds = ((choice as any).addOnVariantIds ?? []) as number[];
  const envelopes = addOnVariantIds.map((variantId) => ({
    id: Number(variantId),
    quantity: 1 as const,
    properties: {
      _giftwrap_key: giftWrapKey,
      ...(parentTitle && { _giftwrap_parent_title: parentTitle }),
      ...(vendor && { _vendorRoutingMatch: vendor })
    }
  }));

  return [card, ...envelopes];
};

interface CreatePayloadOptions {
  choices: ChoicePayload[];
  addons: Addon[];
  visualiserImageUrls: { url: string; groupId: string }[];
  baseProduct: { id: string; quantity: number };
  country: string;
  baseAttributes: Record<string, string>;
  selections: Selections;
  baseProductConfigurationSlotId: string;
  slots: Slot[];
  templateId?: string | null;
  /** The personalisation's cart version (ALBAU-855). Missing = 1. */
  personalisationVersion?: number | string | null;
}

export const createPayload = async ({
  choices,
  addons,
  visualiserImageUrls,
  baseProduct,
  country,
  baseAttributes,
  selections,
  baseProductConfigurationSlotId,
  slots,
  templateId,
  personalisationVersion
}: CreatePayloadOptions): Promise<{
  formData: FormData;
  stringifiedItems: { id: number; quantity: number; properties: Record<string, string | File> }[];
  featuredImageMapping: Record<
    string,
    { groupId: string; lineItemPropertyKey: string; supportingImageLineItemPropertyKey: string; slotType: string }
  >;
  checkId: string;
  featuredImages: Record<string, File>;
  /** The gift wrap key on the Story of Us card and envelope lines of this add, or null when there are none. */
  giftWrapKey: string | null;
}> => {
  // Used to find the item later if we experience a bad connection.
  const checkId = self.crypto.randomUUID();
  // Create visBlob for IMAGE_UPLOAD slots if visualiserImageUrl is available.
  const visData =
    visualiserImageUrls?.map((groupData, index) => {
      const visIsBase64 = groupData.url?.startsWith('data:');
      // If the URL is a base64 string, convert it to a File object.
      // Otherwise, use the URL as is.
      const convertedItem = visIsBase64
        ? base64ToFile({
            base64String: groupData.url?.split('base64,').at(1),
            mimeType: imageType,
            fileName: `visualiser-image-${index}.${imageType.split('/').at(1)}`
          })
        : groupData.url;
      return {
        url: convertedItem,
        groupId: groupData.groupId,
        file: groupData.file,
        lineItemPropertyKey: groupData.lineItemPropertyKey,
        label: groupData.label,
        // Pass through positioning data for image cropping in line item properties
        positioning: groupData.positioning || null
      };
    }) || [];

  const personalisationChoices = buildPersonalisationChoices({
    choices,
    addons,
    baseProductConfigurationSlotId
  });
  const baseProductId = removeGid({ id: `${baseProduct.id}` });
  const baseVariantId =
    addBaseGid({ dataType: 'ProductVariant' }) +
    '/' +
    String(baseProduct.id).replace(addBaseGid({ dataType: 'ProductVariant' }) + '/', '');
  // Get the vendor for the base product to add to the line item properties.
  const vendor = await fetchVendor({ variantId: baseVariantId, country });

  if (!vendor) {
    logError({
      label: 'addToCart',
      error: new Error('Vendor not found'),
      message: 'Vendor not found',
      extraData: { baseProductId },
      throwError: true
    });
  }

  // Convert baseAttributes to an array from an object to a single key value pairs
  const baseAttributesArray = Object.entries(baseAttributes);
  // Flat map over baseAttributes and find any keys with commas in and split them into an two items with the same value.
  const rawProcessedBaseAttributes = baseAttributesArray.reduce(
    (acc, [key, value]) => {
      if (value !== null) {
        // If the key includes a comma, split it into an array of keys and values.
        if (key.includes(',')) {
          const keys = key.split(',');
          keys.forEach((splitKey) => {
            acc[`${splitKey}`] = value;
          });
        } else {
          acc[`${key}`] = value;
        }
      }

      return acc;
    },
    {} as Record<string, string>
  );

  // VAS LIPs (_vas_type, _template_id, _jig_id, ...) are grouped by value at source in
  // processLineItems' setWithSuffix: identical values across grouped slots collapse to a single
  // key, and distinct values are written unsuffixed then _2, _3 (never _1) — per ALBAU-743.
  const processedBaseAttributes = { ...rawProcessedBaseAttributes };

  const { featuredImageMapping, featuredImages } = await imagePropertiesProcessing({
    visData,
    personalisationChoices,
    selections
  });
  const uvInsertImageFiles = personalisationChoices
    .filter((choice) => choice.slotType === 'UV_INSERT')
    .reduce(
      (acc, choice) => {
        if ('uvInsertImageByKey' in choice && choice.uvInsertImageByKey) {
          return { ...acc, ...choice.uvInsertImageByKey };
        }
        return acc;
      },
      {} as Record<string, File>
    );

  // Story of Us (ALBAU-909): the keepsake card and its envelope are their own lines in this same request, linked
  // to the piece by a gift wrap key, as on a gift wrapped order (buildPersonalisationChoices leaves them out of the
  // piece's choices). One key per add to bag, only when the card was picked; other UV inserts get none. The piece
  // carries it in its base attributes, the card and envelope as their own properties with the parent title, the
  // product title as the gift wrap flow sends it.
  const storyOfUsCards = choices.filter(isStoryOfUsCardChoice);
  const giftWrapKey = storyOfUsCards.length > 0 ? generateAlphaNumeric(8) : null;
  const giftWrapParentTitle: string | undefined = (window as any).personalisation?.product?.title || undefined;
  if (giftWrapKey && !giftWrapParentTitle) {
    // Still send the key: carrier-service treats a keyed line with no parent title as the parent, so say so.
    logError({
      label: 'addToCart',
      error: new Error('Product title not found'),
      message: 'Gift wrap key sent without _giftwrap_parent_title',
      extraData: { baseProductId, giftWrapKey }
    });
  }
  // The choice carries only the card's config handle, so read its config from the page, as the journey did.
  const storyOfUsCardLines = giftWrapKey
    ? (() => {
        const uvInserts = resolveUvInserts({ payload: readUvInsertsBootstrapPayload() });
        return storyOfUsCards.flatMap((choice) =>
          buildStoryOfUsCardLines({ choice, uvInserts, giftWrapKey, parentTitle: giftWrapParentTitle, vendor })
        );
      })()
    : [];

  let items = [
    {
      id: Number(baseProductId),
      quantity: Number(baseProduct.quantity),
      properties: {
        ...featuredImages,
        ...uvInsertImageFiles,
        _check_id: checkId,
        _personalisation_choices: personalisationChoices
          // Remap into the format expected by the cart
          .map(
            ({
              choice,
              slotId,
              stepId,
              index,
              lineItemPropertyKey,
              slotType,
              font,
              visualisationGroupId,
              supportingImageLineItemPropertyKey,
              uvInsertProperties,
              uvInsertImageByKey,
              imageUploadProperties
            }) => {
              const requiredProps = {
                c: choice,
                sl: slotId,
                st: stepId,
                i: index
              };

              if (['COMPONENT', 'SEQUENTIAL'].includes(slotType)) {
                requiredProps.c = choice;
              }

              if (slotType === 'ALPHANUMERIC') {
                requiredProps.c = lineItemPropertyKey || '';
                requiredProps.v = choice;
                requiredProps.f = font || null;
                // Store visualisationGroupId for matching in addImageUrls
                if (visualisationGroupId) {
                  requiredProps.g = visualisationGroupId;
                }
              }

              if (slotType === 'IMAGE_UPLOAD') {
                // When a photo variant is configured, choice is "{slotId}:{photoVariantId}" — use it
                // directly so the pricing engine can expand it as an add-on product line (insert SKU).
                // v holds the primary uploaded image CDN URL (filled in after upload by createImageSwitchPayload).
                // properties carries all image LIP keys as placeholders for the warehouse.
                if (typeof choice === 'string' && choice.includes(':')) {
                  requiredProps.c = choice;
                  requiredProps.v = ''; // placeholder — replaced by CDN URL in createImageSwitchPayload
                  // lip records which key is the primary image LIP so createImageSwitchPayload
                  // can populate v correctly. It is removed from the choice once v is set.
                  requiredProps.lip = lineItemPropertyKey || '';

                  // Build properties with image LIP key placeholders.
                  // Primary key → uploaded image; supporting key → visualiser image.
                  // imagePropertiesProcessing assigns the files accordingly.
                  const photoVariantProps: Record<string, string> = {};
                  if (lineItemPropertyKey) {
                    photoVariantProps[lineItemPropertyKey] = '';
                  }
                  if (supportingImageLineItemPropertyKey && !(supportingImageLineItemPropertyKey in photoVariantProps)) {
                    photoVariantProps[supportingImageLineItemPropertyKey] = '';
                  }
                  // Merge the photo variant's _template_id (built in buildPersonalisationChoices) so the
                  // warehouse receives the insert template alongside the image LIP placeholders.
                  if (imageUploadProperties) {
                    Object.assign(photoVariantProps, imageUploadProperties);
                  }
                  requiredProps.properties = photoVariantProps;
                } else {
                  // Legacy IMAGE_UPLOAD (no photo variant): c is the LIP key, v is empty.
                  requiredProps.c = lineItemPropertyKey || '';
                  requiredProps.v = '';
                  // Store supportingImageLineItemPropertyKey for processing original images in addImageUrls.
                  if (supportingImageLineItemPropertyKey) {
                    requiredProps.s = supportingImageLineItemPropertyKey;
                  }
                }
              }

              if (slotType === 'UV_INSERT') {
                // c is already "configHandle:variantId" from buildPersonalisationChoices.
                // Attach the text LIP keys, _template_id, and vendor routing in a nested properties object.
                const uvInsertImagePlaceholders = Object.keys(uvInsertImageByKey || {}).reduce(
                  (acc, key) => ({
                    ...acc,
                    [key]: ''
                  }),
                  {} as Record<string, string>
                );
                const uvInsertChoiceProperties = {
                  ...(uvInsertProperties || {}),
                  ...uvInsertImagePlaceholders,
                  ...(vendor && { _vendorRoutingMatch: vendor })
                };
                if (Object.keys(uvInsertChoiceProperties).length > 0) {
                  requiredProps.properties = uvInsertChoiceProperties;
                }
              }

              return requiredProps;
            }
          ),
        _personalisation_base_attributes: {
          ...processedBaseAttributes,
          ...(vendor && { _vendorRoutingMatch: vendor }),
          // The personalisation's cart version. Lines stamped below the product's current version are flagged
          // in the cart and block checkout (base/cart-version.ts). Kept in base attributes so it survives into
          // checkout, orders and Klaviyo cart recovery.
          [CART_VERSION_KEY]: String(Number(personalisationVersion) || 1),
          // Add variant-level templateId, grouped by value: written as _2, _3 etc. only when it is a
          // DISTINCT value (never _1). If a VAS slot already captured this templateId, skip it.
          ...(templateId &&
            (() => {
              const base = '_template_id';
              if (!(base in processedBaseAttributes)) return { [base]: templateId };
              // eslint-disable-next-line security/detect-object-injection
              if (processedBaseAttributes[base] === templateId) return {};
              let i = 2;
              while (`${base}_${i}` in processedBaseAttributes) {
                // eslint-disable-next-line security/detect-object-injection
                if (processedBaseAttributes[`${base}_${i}`] === templateId) return {};
                i++;
              }
              return { [`${base}_${i}`]: templateId };
            })()),
          // The Story of Us card's gift wrap key, linking the piece to its card and envelope lines.
          ...(giftWrapKey && { _giftwrap_key: giftWrapKey })
        }
      }
    }
  ];

  const validationErrors = items
    .map(({ id, quantity, properties }, index) => validateCartItem({ id, quantity, properties }, index, slots))
    .filter((item) => item !== null);
  if (validationErrors.length > 0) {
    logError({
      label: 'addToCart',
      error: new Error('Validation errors occurred'),
      message: 'Cart validation failed',
      extraData: { validationErrors }
    });
    throw new Error(`Validation errors:\n${validationErrors.join('\n')}`);
  }

  // Stringify all the values for the keys under properties.
  // But skip File objects and plain string IDs - they will be handled separately.
  const stringifiedItems = items.map((item) => {
    return {
      ...item,
      properties: Object.fromEntries(
        Object.entries(item.properties).map(([key, value]) => {
          // Skip stringifying File objects - they will be handled in FormData conversion
          if (value instanceof File) {
            return [key, value];
          }
          // Skip stringifying _check_id - it's already a plain string UUID
          if (['_check_id', '_template_id'].includes(key)) {
            return [key, value];
          }
          return [key, JSON.stringify(value)];
        })
      )
    };
  });
  // The Story of Us card and envelope go after the piece, which must stay item 0 (the _check_id retry and the
  // form data checks read item 0). Their properties are already plain strings and Files, so they are not
  // stringified, and they are not validated as personalised lines: they have no choices.
  stringifiedItems.push(...storyOfUsCardLines);

  const formData = await convertCartPayloadToFormData({
    items: stringifiedItems,
    personalisationChoices,
    sections: ['cart-drawer'],
    visData,
    slots
  });

  return { formData, stringifiedItems, featuredImageMapping, checkId, featuredImages, giftWrapKey };
};
